import { ChevronRight, MapPin, Star } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { getPropertyImageUrl, isPlaceholder, markAsPlaceholder, DEFAULT_IMAGE } from "@/lib/config/cdn";

interface PropertyCardProps {
  title: string;
  location: string;
  views: number;
  inquiries: number;
  price: string;
  featured: boolean;
  image?: string;
  listing_code?: string;
  listingCode?: string;
}

const PropertyCard = ({ 
  title, 
  location, 
  views, 
  inquiries, 
  price, 
  featured, 
  image,
  listing_code,
  listingCode
}: PropertyCardProps) => {
  // Use CDN URL if listing code is available (checking both naming conventions),
  // otherwise use provided image
  const imageUrl = (listingCode || listing_code)
    ? getPropertyImageUrl(listingCode || listing_code)
    : image;

  return (
    <Card>
      <div className="relative">
        <div className="aspect-video w-full overflow-hidden">
          <img 
            src={imageUrl} 
            alt={title} 
            className="h-full w-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              // Only set to placeholder if not already using placeholder and not already tracked
              if (!isPlaceholder(target.src)) {
                target.onerror = null;
                markAsPlaceholder(target.src);
                target.src = DEFAULT_IMAGE;
              }
            }}
          />
        </div>
        {featured && (
          <div className="absolute top-3 left-3 bg-[#F97316] px-2 py-0.5 rounded-full text-xs font-semibold flex items-center text-white gap-1 shadow-md">
            <Star size={10} className="fill-white text-white" />
            Featured
          </div>
        )}
      </div>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">{title}</CardTitle>
        <CardDescription className="flex items-center">
          <MapPin className="h-3 w-3 mr-1" /> 
          {location}
        </CardDescription>
      </CardHeader>
      <CardContent className="pb-2">
        <div className="font-semibold text-lg">{price}</div>
        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
          <div>
            <span className="text-muted-foreground">Views:</span> 
            <span className="font-medium"> {views}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Inquiries:</span> 
            <span className="font-medium"> {inquiries}</span>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button variant="outline" size="sm" className="w-full" asChild>
          <Link to={`/dashboard/listings`}>
            View Details
            <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </Button>
      </CardFooter>
    </Card>
  );
};

export default PropertyCard;
